import { LinearProgress, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  FileUploadStatus,
  useSendMessageContext,
} from 'chat/send_message_context'
import { Image } from 'expo-image'
import React, { useCallback, useMemo } from 'react'
import { ImageSourcePropType, View } from 'react-native'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { PressableOpacity } from 'shared/components/pressable_opacity'

export type AccessoryButtonName = 'photo' | 'camera' | 'upload'

export interface MessageAccessoryBarProps {
  buttonNames: AccessoryButtonName[]
  chatGroupId: string | undefined
}

export const MESSAGE_ACCESSORY_BAR_HEIGHT = 44

const buttonImages: Record<AccessoryButtonName, ImageSourcePropType> = {
  photo: require('assets/chat/photo.png'),
  camera: require('assets/chat/camera.png'),
  upload: require('assets/chat/upload.png'),
}

export function MessageAccessoryBar({
  buttonNames,
  chatGroupId = '',
}: MessageAccessoryBarProps) {
  const styles = useStyles()
  const { addAttachment, getFileUploadStatus, setFileUploadStatus } =
    useSendMessageContext()

  const fileUploadStatus = useMemo(
    () => getFileUploadStatus(chatGroupId),
    [chatGroupId, getFileUploadStatus]
  )

  const setChatFileUploadStatus = useCallback(
    (value: FileUploadStatus) => setFileUploadStatus(chatGroupId, value),
    [chatGroupId, setFileUploadStatus]
  )

  const addChatAttachment = useCallback(
    (name: AccessoryButtonName) => addAttachment(chatGroupId, name),
    [addAttachment, chatGroupId]
  )

  return (
    <UploadProgress
      fileUploadStatus={fileUploadStatus}
      setFileUploadStatus={setChatFileUploadStatus}
    >
      <View style={styles.container} key="NextMessageAccessoryBar">
        {buttonNames.map(name => (
          <AccessoryButton key={name} onPress={addChatAttachment} name={name} />
        ))}
      </View>
    </UploadProgress>
  )
}

const UploadProgress = ({
  fileUploadStatus,
  setFileUploadStatus,
  children,
}: {
  fileUploadStatus: FileUploadStatus
  setFileUploadStatus: React.Dispatch<
    React.SetStateAction<FileUploadStatus | undefined>
  >
  children: React.ReactNode
}) => {
  const styles = useStyles()
  if (fileUploadStatus?.error) {
    return (
      <PressableOpacity
        style={styles.photoUploadError}
        onPress={() => setFileUploadStatus(undefined)}
      >
        <Text style={styles.photoUploadErrorText}>
          {fileUploadStatus.error.message}
        </Text>
        <Text style={styles.photoUploadErrorCloseButton}>X</Text>
      </PressableOpacity>
    )
  }

  if (fileUploadStatus?.progress !== undefined) {
    return (
      <View style={styles.accessoryProgressContainer}>
        <LinearProgress
          color={styles.progressBar.color}
          value={Math.round(Math.min(1, fileUploadStatus.progress))}
        />
        <Text style={styles.photoUploadMessageText}>
          {t('uploadInProgressDescription')}
        </Text>
      </View>
    )
  }

  return <>{children}</>
}

interface AccessoryButtonProps {
  onPress: (name: AccessoryButtonName) => void
  name: AccessoryButtonName
}

function AccessoryButton({ onPress, name }: AccessoryButtonProps) {
  return (
    <TouchableOpacity onPress={() => onPress(name)}>
      <Image source={buttonImages[name]!} style={{ width: 24, height: 24 }} />
    </TouchableOpacity>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    height: MESSAGE_ACCESSORY_BAR_HEIGHT,
    width: '100%',
    backgroundColor: theme.colors?.white,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  photoUploadMessageText: {
    padding: 5,
    marginLeft: 5,
    textAlignVertical: 'center',
    textAlign: 'center',
    fontSize: 12,
    color: theme.colors?.grey3,
  },
  photoUploadError: {
    flexDirection: 'row',
    backgroundColor: theme.colors?.grey5,
    marginHorizontal: 10,
    justifyContent: 'space-between',
  },
  photoUploadErrorText: {
    padding: 5,
    marginLeft: 5,
    textAlignVertical: 'center',
    height: '100%',
    fontSize: 15,
    color: theme.colors?.primary,
  },
  photoUploadErrorCloseButton: {
    width: 25,
    height: 25,
    paddingRight: 10,
    paddingTop: 4,
    textAlign: 'center',
    textAlignVertical: 'center',
    fontSize: 13,
    fontWeight: 'bold',
    color: theme.colors?.grey2,
  },
  accessoryProgressContainer: {
    marginHorizontal: 20,
  },
  progressBar: {
    color: theme.colors?.primary,
  },
}))
